import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Modal, Button, Container, Row, Col, Card, Form, Table } from 'react-bootstrap';
import { BsBank, BsPlusCircleFill, BsListCheck, BsArrowClockwise, BsTrash, BsPencilSquare } from 'react-icons/bs';
import Topbar from './components/Topbar';
import SuperAdminSidebar from './components/SuperAdminSidebar';
import { FaHistory } from 'react-icons/fa';
import { toast } from 'react-toastify';

function FundManagement() {
  const [department, setDepartment] = useState('');
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');

  const [newFundName, setNewFundName] = useState('');
  const [fundSources, setFundSources] = useState([]);

  const [showAddFundModal, setShowAddFundModal] = useState(false);
  const [selectedFund, setSelectedFund] = useState(null);
  const [amountToAdd, setAmountToAdd] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);

  const [showEditFundModal, setShowEditFundModal] = useState(false);
  const [fundNameToEdit, setFundNameToEdit] = useState('');

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [fundToDelete, setFundToDelete] = useState(null);

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedFundForHistory, setSelectedFundForHistory] = useState(null);
  const [singleFundHistory, setSingleFundHistory] = useState([]);

  const [fundHistory, setFundHistory] = useState([]);

  // Get user type from localStorage
  const userType = localStorage.getItem('type');

  const fetchFundSources = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.warn('Authorization token not found. Please log in again.');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_SUPER_ADMIN_API_URL}fundlist.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        const formattedFunds = response.data.funds.map(fund => ({
          id: parseInt(fund.id, 10),
          name: fund.fund_name,
          total: parseFloat(fund.amount) || 0,
        }));
        setFundSources(formattedFunds);
      } else {
        toast.error(`Error fetching funds: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching fund sources:', error);
      toast.error('An error occurred while fetching the fund sources.');
    }
  };

  const fetchFundHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.warn('Authorization token not found. Please log in again.');
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_SUPER_ADMIN_API_URL}fundhistory.php`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.success) {
        const formattedHistory = response.data.fund_historys.map(entry => ({
          id: parseInt(entry.id, 10),
          name: entry.fund_name,
          amount: parseFloat(entry.amount),
          date: entry.date,
          work_order: entry.work_order || null,
        }));
        setFundHistory(formattedHistory);
      } else {
        toast.error(`Error fetching fund history: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error fetching fund history:', error);
      toast.error('An error occurred while fetching the fund history.');
    }
  };

  useEffect(() => {
    fetchFundSources();
    fetchFundHistory();
  }, []);

  const handleCreateFundSource = async (e) => {
    e.preventDefault();
    if (!newFundName) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.warn('Authorization token not found. Please log in again.');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}addfund.php`,
        { fund_name: newFundName },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Fund source created successfully!');
        setNewFundName('');
        fetchFundSources();
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while creating the fund source.');
    }
  };

  const handleDeleteFundSource = (fund) => {
    setFundToDelete(fund);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (fundToDelete) {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          toast.warn('Authorization token not found. Please log in again.');
          return;
        }
        const response = await axios.post(
          `${process.env.REACT_APP_SUPER_ADMIN_API_URL}deletefund.php`,
          { fund_id: fundToDelete.id.toString() },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        if (response.data.success) {
          toast.success('Fund deleted successfully!');
          fetchFundSources();
          fetchFundHistory();
        } else {
          toast.error(`Error deleting fund: ${response.data.message}`);
        }
      } catch (error) {
        toast.error('An error occurred while deleting the fund.');
        console.error('Error deleting fund:', error);
      }
      setShowDeleteModal(false);
      setFundToDelete(null);
    }
  };

  const handleShowAddFundModal = (fund) => {
    setSelectedFund(fund);
    setShowAddFundModal(true);
  };

  const handleCloseAddFundModal = () => {
    setShowAddFundModal(false);
    setSelectedFund(null);
    setAmountToAdd('');
    setDate(new Date().toISOString().split('T')[0]);
  };

  const handleAddFund = async (e) => {
    e.preventDefault();
    if (!selectedFund || !amountToAdd) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.warn('Authorization token not found. Please log in again.');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}addsinglefundamount.php`,
        {
          fund_id: selectedFund.id,
          amount: parseFloat(amountToAdd),
          date: date,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        toast.success('Fund added successfully!');
        
        fetchFundSources();
        fetchFundHistory();

        handleCloseAddFundModal();
      } else {
        toast.error(`Error adding fund: ${response.data.message}`);
      }
    } catch (error) {
      console.error('Error adding fund:', error);
      toast.error('An error occurred while adding the fund.');
    }
  };

  const handleShowEditModal = (fund) => {
    setSelectedFund(fund);
    setFundNameToEdit(fund.name);
    setShowEditFundModal(true);
  };

  const handleCloseEditModal = () => {
    setShowEditFundModal(false);
    setSelectedFund(null);
    setFundNameToEdit('');
  };

  const handleUpdateFundName = async (e) => {
    e.preventDefault();
    if (!selectedFund || !fundNameToEdit) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.warn('Authorization token not found. Please log in again.');
        return;
      }
      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}editfund.php`,
        {
          fund_id: selectedFund.id.toString(),
          fund_name: fundNameToEdit,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      if (response.data.success) {
        toast.success('Fund name updated successfully!');
        fetchFundSources();
        fetchFundHistory();
        handleCloseEditModal();
      } else {
        toast.error(`Error updating fund: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while updating the fund name.');
      console.error('Error updating fund:', error);
    }
  };

  const handleShowHistoryModal = async (fund) => {
    setSelectedFundForHistory(fund);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.warn('Authorization token not found. Please log in again.');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}singlefundhistory.php`,
        { fund_id: fund.id.toString() },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        setSingleFundHistory(response.data.history || []);
      } else if (response.data.message === 'No history found for this fund_id.') {
        setSingleFundHistory([]);
      }
      else {
        toast.error(`Error fetching fund history: ${response.data.message}`);
        setSingleFundHistory([]);
      }
    } catch (error) {
      console.error('Error fetching fund history:', error);
      toast.error('An error occurred while fetching fund history.');
      setSingleFundHistory([]);
    }

    setShowHistoryModal(true);
  };

  const handleCloseHistoryModal = () => {
    setShowHistoryModal(false);
    setSelectedFundForHistory(null);
    setSingleFundHistory([]);
  };

  return (
    <>
      <Topbar />
      <div className="d-flex">
        <SuperAdminSidebar />
        <Container fluid className="p-4 bg-light">
          <header className="mb-4">
            <h1 className="h3 d-flex align-items-center gap-2"> Fund Management
            </h1>
          </header>

          <Row className="g-4 mb-4">
            {/* Only show create fund section if not chairman */}
            {userType !== 'chairman' && (
              <Col lg={4}>
                <Card className="shadow-sm">
                  <Card.Header as="h5" className="d-flex align-items-center gap-2" style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}>
                    <BsPlusCircleFill style={{ color: '#1976d2' }} /> <span style={{ color: '#1976d2' }}>Create New Fund Source</span>
                  </Card.Header>
                  <Card.Body>
                    <Form onSubmit={handleCreateFundSource}>
                      <Form.Group className="mb-3" controlId="newFundName">
                        <Form.Label>Fund Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={newFundName}
                          onChange={(e) => setNewFundName(e.target.value)}
                          placeholder="Enter New Fund name"
                          required
                        />
                      </Form.Group>
                      <div className="d-grid">
                        <Button variant="success" type="submit">
                          Create Fund Source
                        </Button>
                      </div>
                    </Form>
                  </Card.Body>
                </Card>
              </Col>
            )}
            <Col lg={userType === 'chairman' ? 12 : 8}>
              <Card className="h-100 shadow-sm">
                <Card.Header as="h5" className="d-flex align-items-center gap-2" style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}>
                  <BsListCheck style={{ color: '#1976d2' }} /> <span style={{ color: '#1976d2' }}>Available Fund Sources</span>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table striped bordered hover responsive="sm">
                      <thead className="table-dark" style={{ backgroundColor: '#0d6efd' }}>
                        <tr>
                          <th>Fund Name</th>
                          <th>Total Amount (₹)</th>
                          {/* Only show Action column if not chairman */}
                          {userType !== 'chairman' && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {fundSources.map(fund => (
                          <tr key={fund.id}>
                            <td>{fund.name}</td>
                            <td>{fund.total.toLocaleString()}</td>
                            {/* Only show actions if not chairman */}
                            {userType !== 'chairman' && (
                              <td className="d-flex gap-2">
                                <Button variant="primary" size="sm" onClick={() => handleShowAddFundModal(fund)}>
                                  <BsPlusCircleFill className="me-1"/> Add Fund
                                </Button>
                                <Button variant="warning" size="sm" onClick={() => handleShowEditModal(fund)}>
                                  <BsPencilSquare />
                                </Button>
                                <Button variant="info" size="sm" onClick={() => handleShowHistoryModal(fund)}>
                                  <FaHistory />
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => handleDeleteFundSource(fund)}>
                                  <BsTrash />
                                </Button>
                              </td>
                            )}
                            {/* For chairman, only show history button */}
                            {userType === 'chairman' && (
                              <td className="d-flex gap-2">
                                <Button variant="info" size="sm" onClick={() => handleShowHistoryModal(fund)}>
                                  <FaHistory />
                                </Button>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm">
             <Card.Header as="h5" className="d-flex align-items-center gap-2" style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}>
                <BsArrowClockwise style={{ color: '#1976d2' }} /> <span style={{ color: '#1976d2' }}>Fund Additions History</span>
            </Card.Header>
            <Card.Body>
                <div className="table-responsive" style={{ maxHeight: '300px' }}>
                    <Table striped bordered hover responsive="sm">
                        <thead className="table-dark" style={{ backgroundColor: '#0d6efd' }}>
                            <tr>
                                <th>Fund Source</th>
                                <th>Amount Added (₹)</th>
                                <th>Date</th>
                                <th>Work Order</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fundHistory.map((entry, index) => (
                                <tr key={entry.id || index}>
                                    <td>{entry.name}</td>
                                    <td>{entry.amount.toLocaleString()}</td>
                                    <td>{entry.date}</td>
                                    <td>{entry.work_order || 'NIL'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            </Card.Body>
          </Card>
        </Container>
      </div>

      {/* Only show modals if not chairman */}
      {userType !== 'chairman' && (
        <>
          <Modal show={showAddFundModal} onHide={handleCloseAddFundModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="d-flex align-items-center gap-2">
                        <BsPlusCircleFill />
                        <span>Add Fund</span>
                    </div>
                    <small className="text-muted d-block mt-1" style={{ wordBreak: 'break-word' }}>
                        {selectedFund?.name}
                    </small>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleAddFund}>
                    <Form.Group className="mb-3" controlId="amountToAdd">
                        <Form.Label>Amount (₹)</Form.Label>
                        <Form.Control
                            type="number"
                            value={amountToAdd}
                            onChange={(e) => setAmountToAdd(e.target.value)}
                            placeholder="Enter amount to add"
                            required
                            autoFocus
                        />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="dateToAdd">
                        <Form.Label>Date</Form.Label>
                        <Form.Control
                            type="date"
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            required
                        />
                    </Form.Group>
                    <div className="d-grid">
                        <Button variant="primary" type="submit">
                            Confirm Add Fund
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
          </Modal>

          <Modal show={showEditFundModal} onHide={handleCloseEditModal} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <div className="d-flex align-items-center gap-2">
                        <BsPencilSquare />
                        <span>Edit Fund Name</span>
                    </div>
                    <small className="text-muted d-block mt-1" style={{ wordBreak: 'break-word' }}>
                        {selectedFund?.name}
                    </small>
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form onSubmit={handleUpdateFundName}>
                    <Form.Group className="mb-3" controlId="fundNameToEdit">
                        <Form.Label>New Fund Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={fundNameToEdit}
                            onChange={(e) => setFundNameToEdit(e.target.value)}
                            placeholder="Enter new fund name"
                            required
                            autoFocus
                        />
                    </Form.Group>
                    <div className="d-grid">
                        <Button variant="primary" type="submit">
                            Save Changes
                        </Button>
                    </div>
                </Form>
            </Modal.Body>
          </Modal>

          <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center gap-2">
                    <BsTrash style={{ color: '#dc3545' }} /> Confirm Deletion
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>Are you sure you want to delete the fund source "<strong>{fundToDelete?.name}</strong>"?</p>
                <p className="text-danger">This will also remove all associated history and the action cannot be undone.</p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={confirmDelete}>
                    Delete Fund
                </Button>
            </Modal.Footer>
          </Modal>
        </>
      )}

      {/* Always show history modal for all users */}
      <Modal show={showHistoryModal} onHide={handleCloseHistoryModal} centered size="lg">
        <Modal.Header closeButton>
            <Modal.Title>
                <div className="d-flex align-items-center gap-2">
                    <FaHistory />
                    <span>Fund History</span>
                </div>
                <small className="text-muted d-block mt-1" style={{ wordBreak: 'break-word' }}>
                    {selectedFundForHistory?.name}
                </small>
            </Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <div className="table-responsive" style={{ maxHeight: '400px' }}>
                <Table striped bordered hover responsive="sm">
                    <thead className="table-dark">
                        <tr>
                            <th>Amount Added (₹)</th>
                            <th>Date</th>
                            <th>G.O Number</th>
                            <th>Purpose</th>
                            <th>Work Order</th>
                        </tr>
                    </thead>
                    <tbody>
                        {singleFundHistory.length > 0 ?
                            singleFundHistory.map((entry, index) => (
                                <tr key={index}>
                                    <td>{entry.amount.toLocaleString()}</td>
                                    <td>{entry.date}</td>
                                    <td>{entry.go_number || 'N/A'}</td>
                                    <td title={entry.purpose}>
                                        {entry.purpose && entry.purpose.length > 30
                                          ? `${entry.purpose.substring(0, 30)}...`
                                          : entry.purpose || 'N/A'}
                                    </td>
                                    <td>{entry.work_order || 'NIL'}</td>
                                </tr>
                            )) :
                            (
                                <tr>
                                    <td colSpan="5" className="text-center">
                                        No history found for this fund.
                                    </td>
                                </tr>
                            )
                        }
                    </tbody>
                </Table>
            </div>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseHistoryModal}>
                Close
            </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default FundManagement; 